import { useState, useEffect } from 'react';

interface ScrollSpyProps {
  sections: {
    /** Unique id for own reference */
    id: string;
    /** HTML element id */
    elementId: string;
  }[];
  onSectionChange?: (sectionId: string) => void;
}

/**
 * When given a list of html id's of sections, this hook will return the id of the section that is currently in view.
 */
function useScrollSpy({ sections, onSectionChange = () => {} }: ScrollSpyProps) {
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      let maxVisibleHeight = 0;
      let newCurrentSectionId = null;

      sections.forEach(({ id, elementId }) => {
        const element = document.getElementById(elementId);
        if (element) {
          const { top, bottom } = element.getBoundingClientRect();
          const visibleTop = Math.max(top, 0);
          const visibleBottom = Math.min(bottom, window.innerHeight);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);

          if (visibleHeight > maxVisibleHeight) {
            maxVisibleHeight = visibleHeight;
            newCurrentSectionId = id;
          }
        }
      });

      if (newCurrentSectionId !== currentSectionId) {
        setCurrentSectionId(newCurrentSectionId);
        if (newCurrentSectionId !== null) {
          onSectionChange(newCurrentSectionId);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections, onSectionChange, currentSectionId]);

  return { currentSectionId };
}

export { useScrollSpy };
